import React from "react";
import styled from "styled-components";
import { Form as RSuiteForm } from "rsuite";
import { colors } from "../theme/colors";
import { shadows } from "../theme/shadows";

const Form = ({
  children,
  className,
  ...props
}) => {
  return (
    <StyledFormWrapper className={className}>
      <RSuiteForm {...props}>
        {children}
      </RSuiteForm>
    </StyledFormWrapper>
  );
};

// Form Group component for consistent spacing
const FormGroup = ({ children, controlId, className, ...props }) => {
  return (
    <StyledFormGroup
      controlId={controlId}
      className={className}
      {...props}
    >
      {children}
    </StyledFormGroup>
  );
};

// Form Label component
const FormLabel = ({ children, className, ...props }) => {
  return (
    <StyledFormLabel className={className} {...props}>
      {children}
    </StyledFormLabel>
  );
};

// Form Control wrapper for consistent input styling
const FormControl = ({ children, className, ...props }) => {
  return (
    <StyledFormControl className={className} {...props}>
      {children}
    </StyledFormControl>
  );
};

const StyledFormWrapper = styled.div`
  .rs-form-group {
    margin-bottom: 20px;

    .rs-form-control-label {
      margin-bottom: 8px;
      display: block;
      font-weight: 500;
      font-size: 14px;
      color: ${colors.neutral[700]};
    }

    .rs-help-block {
      margin-top: 6px;
      font-size: 12px;
      color: ${colors.neutral[600]};
    }
  }

  /* Input styling */
  .rs-form-control-wrapper {
    .rs-input {
      border-radius: 12px;
      border: 2px solid ${colors.neutral[200]};
      padding: 12px 16px;
      transition: all 0.3s ease;
      min-height: 48px !important;
      height: 48px;
      box-sizing: border-box;
      font-size: 14px;

      &:hover {
        border-color: ${colors.primary.main};
        box-shadow: ${shadows.hover.sm};
      }

      &:focus {
        border-color: ${colors.primary.main};
        box-shadow: ${shadows.focus};
      }
    }

    .rs-input-number {
      border-radius: 12px;
      border: 2px solid ${colors.neutral[200]};
      transition: all 0.3s ease;
      min-height: 48px !important;
      height: 48px;
      box-sizing: border-box;

      .rs-input-number-input {
        padding: 12px 16px;
        font-size: 14px;
      }

      &:hover {
        border-color: ${colors.primary.main};
        box-shadow: ${shadows.hover.sm};
      }

      &:focus-within {
        border-color: ${colors.primary.main};
        box-shadow: ${shadows.focus};
      }
    }
  }

  /* Picker styling */
  .rs-picker-toggle {
    border-radius: 12px !important;
    border: 2px solid ${colors.neutral[200]} !important;
    padding: 12px 16px !important;
    transition: all 0.3s ease !important;
    min-height: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;
    font-size: 14px !important;

    &:hover {
      border-color: ${colors.primary.main} !important;
      box-shadow: ${shadows.hover.sm} !important;
    }

    &:focus,
    &.rs-picker-toggle-active {
      border-color: ${colors.primary.main} !important;
      box-shadow: ${shadows.focus} !important;
    }

    .rs-picker-toggle-placeholder {
      color: ${colors.neutral[500]};
    }

    .rs-picker-toggle-value {
      color: ${colors.neutral[800]};
      font-weight: 500;
    }
  }

  /* Dropdown menu styling */
  .rs-picker-menu {
    border-radius: 12px !important;
    border: none !important;
    box-shadow: ${shadows.lg} !important;
    overflow: hidden !important;
    z-index: 9999 !important;
    max-height: 300px !important;

    .rs-picker-select-menu-item {
      padding: 12px 16px !important;
      transition: all 0.2s ease !important;

      &:hover,
      &.rs-picker-select-menu-item-active {
        background-color: ${colors.primary.main} !important;
        color: white !important;
      }
    }
  }

  /* Toggle styling */
  .rs-toggle {
    .rs-toggle-presentation {
      border-radius: 24px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: ${shadows.hover.sm};
      }
    }

    &.rs-toggle-checked .rs-toggle-presentation {
      background-color: ${colors.primary.main};
    }
  }
`;

const StyledFormGroup = styled(RSuiteForm.Group)`
  margin-bottom: 20px !important;
`;

const StyledFormLabel = styled(RSuiteForm.ControlLabel)`
  margin-bottom: 8px !important;
  display: block !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  color: ${colors.neutral[700]} !important;
`;

const StyledFormControl = styled(RSuiteForm.Control)`
  /* Control-specific styling if needed */
`;

// Export sub-components
Form.Group = FormGroup;
Form.Label = FormLabel;
Form.Control = FormControl;
Form.ControlLabel = FormLabel; // Alias for RSuite compatibility

export default Form;
